import Snippets from "./snippets";
import type {
  dataLayerFunctionInterface,
  googleAnalyticsEventV2,
  googleAnalyticsEventV2External,
  tagManagerArgs,
  tagsInterface
} from "@dealer-center/core-interfaces-third-party-gtm";

// https://analytics.google.com/analytics/web/#/p374145324/realtime/overview?params=_u..nav%3Dmaui

const defaultDatalayerName = "dataLayer";

export const TagManager = {
  dataScript(dataLayer: string) {
    const script = document.createElement("script");
    script.setAttribute("data-testid", "gtm-script");
    script.innerHTML = dataLayer;
    return script;
  },
  gtm(args: tagsInterface) {
    const snippets = Snippets.tags(args);

    const noScript = () => {
      const noscript = document.createElement("noscript");
      noscript.setAttribute("data-testid", "gtm-noscript-frame");
      noscript.innerHTML = snippets.iframe;
      return noscript;
    };

    const script = () => {
      const script = document.createElement("script");
      script.setAttribute("data-testid", "gtm-other-script");
      script.innerHTML = snippets.script;
      return script;
    };

    const dataScript = this.dataScript(snippets.dataLayerVar);

    return {
      noScript,
      script,
      dataScript
    };
  },

  /** Add GTM to the page */
  // https://coxautoinc.sharepoint.com/sites/department-user-experience/SitePages/Common%20Data%20Layer%20Variables%20schema%20for%20Google%20Tag%20Manager.aspx
  initialize({
    gtmId,
    events = {},
    dataLayer,
    dataLayerName = defaultDatalayerName,
    auth = "",
    preview = ""
  }: tagManagerArgs) {
    const gtm = this.gtm({
      id: gtmId,
      events,
      dataLayer,
      dataLayerName,
      auth,
      preview
    });
    if (dataLayer) document.head.appendChild(gtm.dataScript);
    document.head.insertBefore(gtm.script(), document.head.childNodes[0]);
    document.body.insertBefore(gtm.noScript(), document.body.childNodes[0]);
  },
  /** Fire an Event */
  // https://coxautoinc.sharepoint.com/sites/department-user-experience/SitePages/User%20Event%20Tracking%20with%20Google%20Tag%20Manager.aspx
  event(
    incomingEvent: googleAnalyticsEventV2External,
    dataLayerName?: string,
    eventCallback?: () => void,
    eventTimeout = 1000
  ) {
    const _dataLayerName: string = dataLayerName ?? defaultDatalayerName;
    if (window[_dataLayerName as keyof Window]) {
      const event: googleAnalyticsEventV2 = {
        event: "productEvent",
        eventProperties: incomingEvent.eventProperties,
        eventName: incomingEvent.eventName
      };
      return window[_dataLayerName as keyof Window].push({
        eventCallback,
        eventTimeout,
        ...event
      });
    }
  },
  /** Create/update the data layer */
  dataLayer({
    dataLayer,
    dataLayerName = defaultDatalayerName
  }: dataLayerFunctionInterface) {
    if (window[dataLayerName as keyof Window]) {
      return window[dataLayerName as keyof Window].push(dataLayer);
    }
    const snippets = Snippets.dataLayer(dataLayerName, dataLayer);
    const dataScript = this.dataScript(snippets);
    document.head.insertBefore(dataScript, document.head.childNodes[0]);
  }
};
